import { env } from '@/utils/constants/env'
import { errorHandler } from '@/utils/functions/errorHandler'
import { axiosInstance } from '@/utils/lib/axios'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Dialog } from 'alurkerja-ui'
import Swal from 'sweetalert2'
import { successStartEventText } from '@/utils/functions/successStartEventText'

export type ITasklistProcess = {
  camunda_form?: string
  form_key?: string
  id: string
  key: string
  name: string
  version: number
}

export type ITasklistTask = {
  assignee: string
  username?: string | null
  camunda_form?: unknown[]
  form_key: string
  id: string
  name: string
  process_definition_id?: string
  process_name?: string
  process_instance_id: string
  task_definition_key: string
  variables: unknown
  instance_history?: unknown
  created_date?: string
}

export type Payload = {
  instance_history?: unknown
  tasks?: unknown[]
  variables?: unknown
}

export type Requests = ITasklistTask & {
  start_time: string
  business_key: string
  process_definition_key?: string
  payload?: Payload
}

export const TasklistService = {
  useGetProcesses: ({ username }: { username?: string } = {}) => {
    return useQuery<ITasklistProcess[]>({
      queryKey: ['tasklist-processes', username],
      queryFn: async () => {
        const url = new URL(env.apiTasklistBaseUrl + '/api/v1/tasklist/processes')
        if (username) {
          url.searchParams.append('username', username)
        }
        const { data } = await axiosInstance.get(url.toString())
        return data.data
      },
    })
  },

  useGetAllTask: ({ processDefinition }: { processDefinition?: string }) =>
    useQuery<ITasklistTask[], { response: { status: number; data: { message: string; details?: string } } }>({
      queryKey: ['tasklist-all-task', processDefinition],
      queryFn: async () => {
        if (!processDefinition) return []

        try {
          const { data } = await axiosInstance.get(
            env.apiTasklistBaseUrl + `/api/v1/tasklist/${processDefinition}/tasks`,
          )
          return data.data
        } catch (err) {
          throw err as { response: { status: number; data: { message: string; details?: string } } }
        }
      },
    }),

  useGetTask: ({ processDefinition, taskId }: { processDefinition: string; taskId: string }) =>
    useQuery<ITasklistTask>({
      queryKey: ['tasklist-task', processDefinition, taskId],
      queryFn: async () => {
        if (!processDefinition || !taskId) return []

        const { data } = await axiosInstance.get(
          env.apiTasklistBaseUrl + `/api/v1/tasklist/${processDefinition}/tasks/${taskId}`,
        )

        return data.data
      },
    }),

  useStartProcess: <T>({ onSuccess }: { onSuccess: () => void }) =>
    useMutation({
      mutationFn: async ({ processDefinitionKey, variables }: { processDefinitionKey: string; variables: T }) => {
        return await axiosInstance.post(env.apiTasklistBaseUrl + `/api/v1/tasklist/${processDefinitionKey}/start`, {
          variables,
        })
      },
      onSuccess: res => {
        Swal.fire({
          title: 'Success',
          text: successStartEventText(res.data.message ?? 'Start process success'),
          icon: 'success',
        }).then(onSuccess)
      },
      onError: err => {
        const error = err as { data: { message: string } }
        errorHandler(error)

        Swal.fire({
          title: 'Error',
          text: error.data.message ?? 'Something went wrong',
          icon: 'error',
        })
      },
    }),

  useCompleteTask: <T>({ onSuccess }: { onSuccess: () => void }) =>
    useMutation({
      mutationFn: ({
        processDefinitionKey,
        taskId,
        variables,
      }: {
        processDefinitionKey: string
        taskId: string
        variables: T
      }) => {
        return axiosInstance.post(
          env.apiTasklistBaseUrl + `/api/v1/tasklist/${processDefinitionKey}/tasks/${taskId}/complete`,
          {
            variables,
          },
        )
      },

      onSuccess: res => {
        Swal.fire({
          title: 'Success',
          text: res.data.message ?? 'Complete task success',
          icon: 'success',
        }).then(onSuccess)
      },

      onError: err => {
        const error = err as { data: { message: string } }
        errorHandler(error)

        Swal.fire({
          title: 'Error',
          text: error.data.message ?? 'Something went wrong',
          icon: 'error',
        })
      },
    }),

  useDownloadFile: () =>
    useMutation({
      mutationFn: async ({
        processDefinitionKey,
        processInstanceId,
        name,
        urlDownload,
      }: {
        processDefinitionKey?: string
        processInstanceId?: string
        name?: string
        urlDownload?: string
      }) => {
        let urlToDownload: URL

        if (!urlDownload?.includes('minio') && !urlDownload?.includes('engine-rest/history')) {
          urlToDownload = new URL(env.apiTasklistBaseUrl + '/api/v1/tasklist' + urlDownload)
        } else if (urlDownload?.includes('minio') || urlDownload?.includes('engine-rest/history')) {
          urlToDownload = new URL(urlDownload)
        } else {
          if (!processDefinitionKey && !processInstanceId && !name) {
            throw new Error('Missing processDefinitionKey or processInstanceId or name')
          }

          urlToDownload = new URL(
            env.apiTasklistBaseUrl + `/api/v1/tasklist/${processDefinitionKey}/download/${processInstanceId}/${name}`,
          )
        }

        return axiosInstance.get(urlToDownload.toString(), { responseType: 'blob' })
      },
      onError: errorHandler,
      onSuccess: (data, variables) => {
        let fileExtension = '.pdf' // default

        const contentType = data.headers['content-type']

        if (contentType.includes('image')) {
          fileExtension = '.png'
        } else if (contentType.includes('sheet')) {
          fileExtension = '.xlsx'
        } else if (contentType.includes('zip')) {
          fileExtension = '.zip'
        }

        const url = window.URL.createObjectURL(new Blob([data.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', variables.name + fileExtension)
        document.body.appendChild(link)
        link.click()
      },
    }),
  useGetMyTasks: ({ processKey = '', page = '0' }: { processKey?: string; page?: string } = {}) =>
    useQuery<ITasklistTask[]>({
      queryKey: ['my-task-tasklist', processKey, page],
      queryFn: async () => {
        const url = new URL(env.apiTasklistBaseUrl + `/api/v1/tasklist/tasks?processKey=${processKey}&page=${page}`)
        const { data } = await axiosInstance.get(url.toString())

        return data.data
      },
    }),
  useGetMyRequest: ({ processKey = '', page = '0' }: { processKey?: string; page?: string } = {}) =>
    useQuery<Requests[]>({
      queryKey: ['my-request-tasklist', processKey, page],
      queryFn: async () => {
        const url = new URL(
          env.apiTasklistBaseUrl + `/api/v1/tasklist/my-request?processKey=${processKey}&page=${page}`,
        )
        const { data } = await axiosInstance.get(url.toString())

        return data.data
      },
    }),
  useGetCompletedMyRequest: ({
    username = '',
    processKey = '',
    page = '0',
  }: { username?: string; processKey?: string; page?: string } = {}) =>
    useQuery<Requests[]>({
      queryKey: ['my-completed-request-tasklist', processKey, page],
      queryFn: async () => {
        const url = new URL(env.apiTasklistBaseUrl + `/api/v1/tasklist/completed-processes?page=${page}`)
        url.searchParams.append('orderBy', 'id')
        url.searchParams.append('order', '0')
        if (processKey) {
          url.searchParams.append('processDefinitionKey', processKey)
        }
        if (username) {
          url.searchParams.append('username', username)
        }
        const { data } = await axiosInstance.get(url.toString())

        return data.data.result
      },
    }),
  useGetDetailCompletedMyRequest: ({ idProcess, username = '' }: { idProcess: number; username?: string }) =>
    useQuery<Requests>({
      queryKey: ['my-completed-detail-request-tasklist'],
      queryFn: async () => {
        const url = new URL(env.apiTasklistBaseUrl + `/api/v1/tasklist/completed-processes/${idProcess}`)
        if (username) {
          url.searchParams.append('username', username)
        }
        const { data } = await axiosInstance.get(url.toString())

        return data.data
      },
    }),
  useGetCandidateTasks: ({ processKey = '', page = '0' }: { processKey?: string; page?: string } = {}) =>
    useQuery<ITasklistTask[]>({
      queryKey: ['candidate-tasks', processKey, page],
      queryFn: async () => {
        const url = new URL(
          env.apiTasklistBaseUrl + `/api/v1/tasklist/candidate-tasks?processKey=${processKey}&page=${page}`,
        )
        const { data } = await axiosInstance.get(url.toString())

        return data.data
      },
    }),
  useClaimTask: ({ onSuccess: onSuccessCallback }: { onSuccess: () => void }) =>
    useMutation({
      mutationFn: ({ processDefinitionKey, taskId }: { processDefinitionKey: string; taskId: string }) => {
        return axiosInstance.post(
          env.apiTasklistBaseUrl + `/api/v1/tasklist/${processDefinitionKey}/tasks/${taskId}/claim`,
        )
      },

      onSuccess: () => {
        onSuccessCallback()
      },

      onError: err => {
        const error = err as { response: { data: { message: string } } }

        Dialog.error({ description: error.response.data.message ?? 'Something went wrong' })
      },
    }),
  useUnclaimTask: ({ onSuccess: onSuccessCallback }: { onSuccess: () => void }) =>
    useMutation({
      mutationFn: ({ processDefinitionKey, taskId }: { processDefinitionKey: string; taskId: string }) => {
        return axiosInstance.post(
          env.apiTasklistBaseUrl + `/api/v1/tasklist/${processDefinitionKey}/tasks/${taskId}/unclaim`,
        )
      },

      onSuccess: () => {
        onSuccessCallback()
      },

      onError: err => {
        const error = err as { response: { data: { message: string } } }

        Dialog.error({ description: error.response.data.message ?? 'Something went wrong' })
      },
    }),
  useDelegateTask: <T>({ onSuccess: onSuccessCallback }: { onSuccess: () => void }) =>
    useMutation({
      mutationFn: ({
        processDefinitionKey,
        taskId,
        body,
      }: {
        processDefinitionKey: string
        taskId: string
        body: T
      }) => {
        return axiosInstance.post(
          env.apiTasklistBaseUrl + `/api/v1/tasklist/${processDefinitionKey}/tasks/${taskId}/delegate`,
          body,
        )
      },

      onSuccess: res => {
        Swal.fire({
          title: 'Success',
          text: res.data.message ?? 'Complete task success',
          icon: 'success',
        }).then(onSuccessCallback)
      },

      onError: err => {
        const error = err as { response: { data: { message: string } } }

        Dialog.error({ description: error.response.data.message ?? 'Something went wrong' })
      },
    }),

  useDeleteProcessInstance: ({ onSuccess: onSuccessCallback }: { onSuccess: () => void }) =>
    useMutation({
      mutationFn: ({ processInstanceId }: { processInstanceId: string }) => {
        return axiosInstance.delete(env.apiTasklistBaseUrl + `/api/v1/tasklist/process-instance/${processInstanceId}`)
      },

      onSuccess: res => {
        Swal.fire({
          title: 'Success',
          text: res.data.message ?? 'Cancel task success',
          icon: 'success',
          timer: 1500,
        }).then(onSuccessCallback)
      },

      onError: err => {
        const error = err as { response: { data: { message: string } } }

        Dialog.error({ description: error.response.data.message ?? 'Something went wrong' })
      },
    }),
}
